import { useRef, useState } from "react";
import { useFetch } from "../../shared/useFetch.js";
import { Button, cn, focusRing, useToast } from "../../ui/index.js";
import { precheckLogo, UPLOAD_TIMEOUT_MS, uploadErrorText } from "./leagueLogoUpload.js";

type LibraryItem = { id: string; url: string };

export const DEFAULT_HINT =
  "Tablo va BIG'dagi liga logosi o'rniga chiqadi. PNG, JPG, WebP yoki SVG, 5 MB gacha. Yuklangan rasmlar kutubxonada qoladi — boshqa o'yinda ham shu yerdan tanlanadi.";

/** Per-match league logo: pick from the uploaded library or upload a new one.
 *  `null` = the theme's own competition logo. */
export function LeagueLogoPicker({
  value,
  onChange,
  emptyLabel = "Tema logosi",
  resetLabel = "Tema logosiga qaytarish",
  hint = DEFAULT_HINT,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  emptyLabel?: string;
  resetLabel?: string;
  hint?: string;
}) {
  const { toast } = useToast();
  const library = useFetch<LibraryItem[]>("/api/league-logos");
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const failed = (text: string) => {
    setError(text);
    toast({ tone: "error", title: "Yuklab bo'lmadi", description: text });
  };

  const upload = async (file: File) => {
    setError(null);
    const local = precheckLogo(file);
    if (local) {
      failed(local);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), UPLOAD_TIMEOUT_MS);
    try {
      const res = await fetch("/api/league-logos", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
        signal: abort.signal,
      });
      const body = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
      if (!res.ok || !body?.url) {
        failed(
          uploadErrorText({ status: res.status, code: body?.error ?? null, sizeBytes: file.size }),
        );
        return;
      }
      onChange(body.url);
      library.refetch();
    } catch {
      failed(
        uploadErrorText({
          status: null,
          code: abort.signal.aborted ? "timeout" : null,
          sizeBytes: file.size,
        }),
      );
    } finally {
      clearTimeout(timer);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const items = library.data ?? [];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md border border-border bg-surface-muted/60">
          {value ? (
            <img src={value} alt="" className="max-h-12 max-w-12 object-contain" />
          ) : (
            <span className="px-1 text-center text-[10px] leading-tight text-ink-faint">
              {emptyLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? "Yuklanmoqda…" : "Rasm yuklash"}
          </Button>
          {value && (
            <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
              {resetLabel}
            </Button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
        />
      </div>
      {error && <p className="text-[12px] font-medium text-danger-strong">{error}</p>}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              title="Shu rasmni tanlash"
              onClick={() => onChange(it.url)}
              className={cn(
                "grid h-11 w-11 place-items-center rounded-md border bg-surface-muted/60",
                focusRing,
                value === it.url
                  ? "border-accent-strong ring-2 ring-accent-strong/50"
                  : "border-border",
              )}
            >
              <img src={it.url} alt="" className="max-h-9 max-w-9 object-contain" />
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-ink-faint">{hint}</p>
    </div>
  );
}
